import { mediaElementSourceNodes } from './constants.js';
import { StudioError } from '#~/error/index.js';
import { logger } from '#~/logger/index.js';

/**
 * You can only create a single media element source per media element. It is also difficult, if not
 * impossible to obtain a reference to the media element source once it has been created. This
 * function provides a way to obtain a reference to the media element source for a given media
 * element. This helper function allows you to cache media element sources and retrieve them a later
 * time.
 *
 * @param context - Audio context.
 * @param audioElement - HTML Audio element.
 * @returns Media element source.
 */
export const getMediaElementSource = (
  context: AudioContext,
  audioElement: HTMLAudioElement
): MediaElementAudioSourceNode => {
  let node = mediaElementSourceNodes.get(audioElement);

  if (!node) {
    node = context.createMediaElementSource(audioElement);

    // Route `<audio>` element to master fader.
    mediaElementSourceNodes.set(audioElement, node);
  }

  return node;
};

/**
 * Determines if the specified audio context is running.
 *
 * @param context - Audio context to check.
 * @returns `true` if the audio context is running, `false` otherwise.
 */
export const isRunning = (context: AudioContext): boolean => context.state === 'running';

/**
 * Determines if the specified audio context is suspended.
 *
 * @param context - Audio context to check.
 * @returns `true` if the audio context is suspended, `false` otherwise.
 */
export const isSuspended = (context: AudioContext): boolean => context.state === 'suspended';

/**
 * Resumes the specified audio context, if necessary.
 *
 * @param context - Audio context to resume.
 * @returns `true` if the audio context is running; `false` otherwise.
 * @throws {never} Does not throw an exception.
 * @see https://bit.ly/3JXOxy9
 */
export const resume = async (context: AudioContext): Promise<boolean> => {
  if (!isSuspended(context)) {
    return true;
  }

  try {
    await context.resume();
  } catch (cause) {
    logger.error(new StudioError('Unable to resume audio context.', { cause }));
  }

  return isRunning(context);
};
